import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getFullEmployeePhotoUrl, useFetchEmployeeByIdQuery } from "../../store/api/employeesApi";
import "./admin.css";

const DEPT_COLORS = [
  "#6366f1", "#0ea5e9", "#10b981", "#f59e0b",
  "#f43f5e", "#8b5cf6", "#14b8a6", "#ec4899",
];
const deptColor = (dept: string) => DEPT_COLORS[dept.charCodeAt(0) % DEPT_COLORS.length];

const STATUS_STYLES: Record<string, { bg: string; color: string; dot: string }> = {
  active:   { bg: "#d1fae5", color: "#065f46", dot: "#10b981" },
  inactive: { bg: "#fee2e2", color: "#9f1239", dot: "#f43f5e" },
  "on-leave": { bg: "#fef3c7", color: "#92400e", dot: "#f59e0b" },
};

const Field: React.FC<{ label: string; value?: string | null; icon?: string }> = ({
  label, value, icon,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#94a3b8" }}>
      {label}
    </span>
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.88rem", color: value ? "#0f172a" : "#cbd5e1", fontWeight: value ? 500 : 400 }}>
      {icon && <i className={`bi ${icon}`} style={{ color: "#94a3b8", fontSize: "0.8rem" }} />}
      {value || "—"}
    </div>
  </div>
);

const SectionCard: React.FC<{ icon: string; title: string; children: React.ReactNode }> = ({
  icon, title, children,
}) => (
  <div style={{ background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
    <div style={{ padding: "12px 18px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 26, height: 26, borderRadius: 7, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <i className={`bi ${icon}`} style={{ fontSize: "0.78rem", color: "#64748b" }} />
      </div>
      <span style={{ fontWeight: 700, fontSize: "0.84rem", color: "#334155" }}>{title}</span>
    </div>
    <div style={{ padding: "16px 18px" }}>{children}</div>
  </div>
);

const ViewEmployee: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const empId = Number(id);

  const { data: emp, isLoading, isError } = useFetchEmployeeByIdQuery(empId, {
    skip: !empId || Number.isNaN(empId),
  });

  const [aadhaarRevealed, setAadhaarRevealed] = useState(false);

  if (isLoading) {
    return (
      <section className="admin-page">
        <div className="dash-empty-state">
          <span className="spinner-border text-secondary" role="status" />
          <span>Loading employee…</span>
        </div>
      </section>
    );
  }

  if (isError || !emp) {
    return (
      <section className="admin-page">
        <div className="dash-empty-state" style={{ color: "#e11d48" }}>
          <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "2rem" }} />
          <span>Employee not found.</span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm mt-2"
            onClick={() => navigate("/admin/employees")}
          >
            Back to Employees
          </button>
        </div>
      </section>
    );
  }

  const status    = (emp.status ?? "active").toLowerCase();
  const sc        = STATUS_STYLES[status] ?? STATUS_STYLES.active;
  const color     = deptColor(emp.department || "A");
  const bg        = `${color}18`;
  const photoSrc  = getFullEmployeePhotoUrl(emp.profilePhotoUrl);
  const aadhaarSrc = getFullEmployeePhotoUrl(emp.aadhaarPhotoUrl);

  const initials = `${emp.firstName.charAt(0)}${emp.lastName.charAt(0)}`.toUpperCase();

  const capitalize = (s: string | null | undefined) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : null;

  const formatDate = (d: string | null | undefined) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return d; }
  };

  const formatAadhaar = (n: string | null | undefined) => {
    if (!n) return null;
    const digits = n.replace(/\D/g, "");
    return digits.length === 12
      ? `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`
      : n;
  };

  return (
    <section className="admin-page">
      <div className="container-fluid px-0">
        <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a" }}>

          {/* ── Gradient header bar ───────────────────────────── */}
          <div style={{ background: "linear-gradient(135deg, #1e1b4b, #4338ca, #6366f1)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="bi bi-person-vcard" style={{ color: "#fff", fontSize: 14 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff", lineHeight: 1.2 }}>Employee Profile</div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}>
                  {emp.firstName} {emp.lastName} · {emp.department}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 8, padding: "4px 12px", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}
                onClick={() => navigate("/admin/employees")}
              >
                <i className="bi bi-arrow-left" /> Back
              </button>
              <Link
                to={`/admin/employees/${emp.id}/edit`}
                style={{ background: "rgba(255,255,255,0.9)", color: "#4338ca", borderRadius: 8, padding: "4px 14px", fontSize: "0.8rem", fontWeight: 700, textDecoration: "none", display: "flex", alignItems: "center", gap: 5 }}
              >
                <i className="bi bi-pencil-square" /> Edit
              </Link>
            </div>
          </div>

          <div className="p-4">

            {/* ── Identity hero card ────────────────────────────── */}
            <div style={{ display: "flex", gap: 20, alignItems: "flex-start", marginBottom: 20, padding: "20px 24px", background: "linear-gradient(135deg, #f8faff, #eef2ff)", border: "1.5px solid #e0e7ff", borderRadius: 12, flexWrap: "wrap" }}>
              {/* Avatar */}
              <div style={{ position: "relative", flexShrink: 0 }}>
                <div style={{ width: 88, height: 88, borderRadius: "50%", overflow: "hidden", background: photoSrc ? undefined : bg, color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.8rem", fontWeight: 800, border: "3px solid #fff", boxShadow: "0 4px 14px #00000018" }}>
                  {photoSrc
                    ? <img src={photoSrc} alt={`${emp.firstName} ${emp.lastName}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : initials}
                </div>
                {/* Status dot */}
                <span style={{ position: "absolute", bottom: 4, right: 4, width: 16, height: 16, borderRadius: "50%", background: sc.dot, border: "2px solid #fff" }} />
              </div>

              {/* Name + meta */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0f172a", marginBottom: 4 }}>
                  {emp.firstName} {emp.lastName}
                </div>
                <div style={{ fontSize: "0.84rem", color: "#475569", marginBottom: 8, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  {emp.role && <span><i className="bi bi-briefcase me-1" style={{ color: "#94a3b8" }} />{emp.role}</span>}
                  {emp.department && (
                    <span style={{ background: bg, color, padding: "1px 10px", borderRadius: 20, fontWeight: 700, fontSize: "0.76rem" }}>
                      {emp.department}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ background: sc.bg, color: sc.color, borderRadius: 20, padding: "2px 12px", fontSize: "0.76rem", fontWeight: 700 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: sc.dot, display: "inline-block", marginRight: 5 }} />
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </span>
                  <span style={{ fontSize: "0.76rem", color: "#94a3b8" }}>ID #{emp.id}</span>
                  {emp.joinedAt && (
                    <span style={{ fontSize: "0.76rem", color: "#94a3b8" }}>
                      <i className="bi bi-calendar-event me-1" />Joined {formatDate(emp.joinedAt)}
                    </span>
                  )}
                </div>
              </div>

              {/* Quick contact */}
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                {emp.email && (
                  <a href={`mailto:${emp.email}`} title={emp.email} style={{ width: 38, height: 38, borderRadius: 10, background: "#fff", border: "1.5px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", textDecoration: "none", fontSize: "0.9rem" }}>
                    <i className="bi bi-envelope" />
                  </a>
                )}
                {emp.phone && (
                  <a href={`tel:${emp.phone}`} title={emp.phone} style={{ width: 38, height: 38, borderRadius: 10, background: "#fff", border: "1.5px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", textDecoration: "none", fontSize: "0.9rem" }}>
                    <i className="bi bi-telephone" />
                  </a>
                )}
              </div>
            </div>

            {/* ── Detail sections ────────────────────────────────── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 16 }}>

              {/* Personal Details */}
              <SectionCard icon="bi-person-lines-fill" title="Personal Details">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}>
                  <Field label="First Name"     value={emp.firstName}                  icon="bi-person" />
                  <Field label="Last Name"      value={emp.lastName}                   icon="bi-person" />
                  <Field label="Gender"         value={capitalize(emp.gender)}         icon="bi-gender-ambiguous" />
                  <Field label="Marital Status" value={capitalize(emp.maritalStatus)}  icon="bi-heart" />
                </div>
              </SectionCard>

              {/* Contact Information */}
              <SectionCard icon="bi-telephone-fill" title="Contact Information">
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "14px" }}>
                  <Field label="Email Address" value={emp.email} icon="bi-envelope" />
                  <Field label="Phone Number"  value={emp.phone} icon="bi-telephone" />
                </div>
              </SectionCard>

              {/* Employment Details */}
              <SectionCard icon="bi-briefcase-fill" title="Employment Details">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}>
                  <Field label="Department"  value={emp.department}             icon="bi-building" />
                  <Field label="Role"        value={emp.role}                   icon="bi-person-badge" />
                  <Field label="Status"      value={capitalize(status)}         icon="bi-activity" />
                  <Field label="Joined Date" value={formatDate(emp.joinedAt)}   icon="bi-calendar-event" />
                  {emp.aadhaarNumber && (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <Field label="Aadhaar Number" value={formatAadhaar(emp.aadhaarNumber)} icon="bi-credit-card-2-front" />
                    </div>
                  )}
                </div>
              </SectionCard>

              {/* Account Metadata */}
              <SectionCard icon="bi-clock-history" title="Record Info">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}>
                  <Field label="Created At"     value={formatDate(emp.createdAt)}  icon="bi-calendar-plus" />
                  <Field label="Last Updated"   value={formatDate(emp.updatedAt)}  icon="bi-pencil-square" />
                </div>
              </SectionCard>
            </div>

            {/* ── Documents ─────────────────────────────────────── */}
            {(photoSrc || aadhaarSrc) && (
              <SectionCard icon="bi-images" title="Identity Documents">
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>

                  {/* Profile photo */}
                  {photoSrc && (
                    <div style={{ flex: "0 0 auto" }}>
                      <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#94a3b8", marginBottom: 8 }}>Profile Photo</div>
                      <img
                        src={photoSrc}
                        alt="Profile"
                        style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 10, border: "1.5px solid #e2e8f0" }}
                      />
                    </div>
                  )}

                  {/* Aadhaar photo */}
                  {aadhaarSrc && (
                    <div style={{ flex: "0 0 auto" }}>
                      <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#94a3b8", marginBottom: 8 }}>
                        Aadhaar Card
                        <button
                          type="button"
                          onClick={() => setAadhaarRevealed((v) => !v)}
                          style={{ marginLeft: 8, background: "none", border: "none", color: "#6366f1", fontSize: "0.7rem", fontWeight: 700, cursor: "pointer", padding: 0 }}
                        >
                          {aadhaarRevealed ? "Hide" : "Reveal"}
                        </button>
                      </div>
                      <div style={{ position: "relative", width: 160, height: 100 }}>
                        <img
                          src={aadhaarSrc}
                          alt="Aadhaar"
                          style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 10, border: "1.5px solid #e2e8f0", filter: aadhaarRevealed ? "none" : "blur(8px)", transition: "filter 0.2s" }}
                        />
                        {!aadhaarRevealed && (
                          <div
                            onClick={() => setAadhaarRevealed(true)}
                            style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", borderRadius: 10 }}
                          >
                            <i className="bi bi-eye-slash" style={{ fontSize: "1.2rem", color: "#475569" }} />
                            <span style={{ fontSize: "0.7rem", color: "#475569", marginTop: 4, fontWeight: 600 }}>Click to reveal</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

          </div>
        </div>
      </div>
    </section>
  );
};

export default ViewEmployee;
